import React, { useState, useEffect, useRef, useCallback } from 'react';
import VeritageLogo from './components/VeritageLogo';
import WaveLines from './components/WaveLines';
import { ArrowRight, ArrowUpRight, Check, Close, Copy, Warn } from './components/Icons';
import { collectClaimEntries } from './claimLabels';
import './App.css';

const API_BASE    = process.env.REACT_APP_API_BASE_URL ?? process.env.REACT_APP_API_URL ?? '';
const VERIFY_BASE = process.env.REACT_APP_VERIFY_BASE_URL ?? 'https://identity.veritage.io';
const DOCS_URL    = process.env.REACT_APP_DOCS_URL ?? 'https://docs.veritage.io';
const NGROK_HEADER = API_BASE.includes('ngrok') ? { 'ngrok-skip-browser-warning': '1' } : {};

const WALLETS = ['EUDI Wallet', 'Google Wallet', 'Apple Wallet'];

const MODES = {
  iframe: {
    label: 'Iframe',
    title: 'Embedded overlay',
  },
  redirect: {
    label: 'Redirect',
    title: 'Full-page redirect',
    body: 'The user is sent to the hosted verification page and returned here with a one-time token.',
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function rewriteHost(url) {
  if (process.env.REACT_APP_VERIFY_BASE_URL) {
    const base = VERIFY_BASE.replace(/\/$/, '');
    return url.replace(/^https?:\/\/[^/]+/, base);
  }
  return url;
}

function requestPreview(mode) {
  return [
    'POST /api/v1/sessions',
    'X-Api-Key: pk_live_…',
    '',
    '{',
    `  "mode": "${mode}",`,
    mode === 'redirect' ? '  "userReference": "demo-visitor",' : '  "userReference": "demo-visitor"',
    ...(mode === 'redirect' ? ['  "redirectUrl": "https://your.app/return"'] : []),
    '}',
  ].join('\n');
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  const [mode, setMode]         = useState('iframe');   // 'iframe' | 'redirect'
  const [step, setStep]         = useState('home');     // home | loading | verifying | result | error
  const [verificationUrl, setVerificationUrl] = useState(null);
  const [identity, setIdentity] = useState(null);
  const [claims, setClaims]     = useState(null);
  const [wallet, setWallet]     = useState(null);
  const [rawResponse, setRawResponse] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const iframeRef = useRef(null);

  const applyResult = useCallback((data) => {
    if (data.verified) {
      setIdentity(data.identity);
      setClaims(data.claims);
      setWallet(data.wallet);
      setRawResponse(data);
      setStep('result');
    } else {
      setErrorMsg('Verification could not be confirmed. Please try again.');
      setStep('error');
    }
  }, []);

  const reset = useCallback(() => {
    setStep('home');
    setIdentity(null);
    setClaims(null);
    setWallet(null);
    setRawResponse(null);
    setVerificationUrl(null);
  }, []);

  // ── Handle redirect return (?token= in URL) ───────────────────────────────
  // When redirect mode completes, the verify app redirects back here with ?token=...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) return;

    // Clean the URL so a refresh doesn't re-exchange
    window.history.replaceState({}, '', window.location.pathname);

    setMode('redirect');
    setStep('loading');
    fetch(`${API_BASE}/api/demo/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...NGROK_HEADER },
      body: JSON.stringify({ verificationToken: token }),
    })
      .then(r => r.json())
      .then(applyResult)
      .catch(() => {
        setErrorMsg('Failed to confirm verification. Please try again.');
        setStep('error');
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Initiate session ──────────────────────────────────────────────────────
  const startVerification = useCallback(async () => {
    setStep('loading');
    setErrorMsg(null);

    // For redirect mode pass the current page URL as the return destination
    const returnUrl = mode === 'redirect'
      ? `${window.location.origin}${window.location.pathname}`
      : undefined;

    try {
      const res = await fetch(`${API_BASE}/api/demo/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...NGROK_HEADER },
        body: JSON.stringify({
          userReference: 'demo-visitor',
          origin: window.location.origin,
          mode,
          ...(returnUrl && { returnUrl }),
        }),
      });
      if (!res.ok) throw new Error('Failed to start session');
      const data = await res.json();

      const url = rewriteHost(data.verificationUrl);
      setVerificationUrl(url);

      if (mode === 'redirect') {
        // Full-page redirect — no iframe needed
        window.location.href = url;
      } else {
        setStep('verifying');
      }
    } catch (e) {
      setErrorMsg(e.message || 'Something went wrong. Please try again.');
      setStep('error');
    }
  }, [mode]);

  // ── Handle postMessage from verify iframe ─────────────────────────────────
  useEffect(() => {
    if (mode !== 'iframe') return;

    const onMessage = async (event) => {
      const allowedOrigins = new Set(['http://localhost:3001', window.location.origin]);
      try { allowedOrigins.add(new URL(VERIFY_BASE).origin); } catch {}
      if (verificationUrl) {
        try { allowedOrigins.add(new URL(verificationUrl).origin); } catch {}
      }
      if (!allowedOrigins.has(event.origin)) return;
      if (!event.data || typeof event.data !== 'object') return;

      const { type, verificationToken } = event.data;

      if (type === 'veritage-verification-complete' && verificationToken) {
        setStep('loading');
        try {
          const res = await fetch(`${API_BASE}/api/demo/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...NGROK_HEADER },
            body: JSON.stringify({ verificationToken }),
          });
          applyResult(await res.json());
        } catch {
          setErrorMsg('Failed to confirm verification. Please try again.');
          setStep('error');
        }
      } else if (type === 'veritage-verification-failed' || type === 'veritage-verification-error') {
        setErrorMsg(event.data.error || 'Verification failed. Please try again.');
        setStep('error');
      } else if (type === 'veritage-verification-back') {
        setVerificationUrl(null);
        setStep('home');
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [mode, verificationUrl, applyResult]);

  // Esc closes the iframe overlay
  useEffect(() => {
    if (step !== 'verifying') return;
    const onKey = (e) => { if (e.key === 'Escape') reset(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, reset]);

  // ── Screens ───────────────────────────────────────────────────────────────
  return (
    <div className="pg">
      <div className="pg-backdrop" aria-hidden="true">
        <WaveLines className="pg-waves" lines={34} amplitude={120} opacity={0.7} animate />
      </div>

      <header className="vt-nav pg-nav">
        <a className="pg-brand" href="https://veritage.io" aria-label="Veritage">
          <VeritageLogo height={28} />
          <span className="pg-brand-divider" aria-hidden="true" />
          <span className="pg-brand-title">Identity Playground</span>
        </a>
        <nav className="pg-nav-links" aria-label="Veritage">
          <a className="vt-nav-link" href={DOCS_URL}>Docs</a>
        </nav>
        <a className="vt-btn vt-btn--sm pg-nav-cta" href="https://app.veritage.io">
          Get API keys
          <span className="vt-btn-arrow"><ArrowRight size={14} /></span>
        </a>
      </header>

      <main className="pg-main">
        <section className="pg-intro">
          <h1 className="pg-title">
            Verify identity with<br />
            <Rotator words={WALLETS} />
          </h1>

          <ol className="pg-how">
            <li><span>1</span><div><strong>Create a session</strong>Your backend calls <code>/api/v1/sessions</code>.</div></li>
            <li><span>2</span><div><strong>User presents a credential</strong>From the wallet they already have.</div></li>
            <li><span>3</span><div><strong>Validate the token</strong>Get verified identity data back.</div></li>
          </ol>
        </section>

        <section className="pg-stage" aria-live="polite">
          {step === 'home' || step === 'verifying' ? (
            <div className="pg-card pg-card--home">
              <div className="vt-eyebrow pg-muted-eyebrow">Integration mode</div>
              <div className="pg-modes" role="radiogroup" aria-label="Integration mode">
                {Object.entries(MODES).map(([id, m]) => (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={mode === id}
                    className={`pg-mode${mode === id ? ' is-active' : ''}`}
                    onClick={() => setMode(id)}
                  >
                    <span className="pg-mode-radio" aria-hidden="true" />
                    <span className="pg-mode-text">
                      <span className="pg-mode-label">{m.label}</span>
                      <span className="pg-mode-title">{m.title}</span>
                    </span>
                  </button>
                ))}
              </div>
              {MODES[mode].body && <p className="pg-mode-body">{MODES[mode].body}</p>}

              <CodePreview code={requestPreview(mode)} />

              <button className="vt-btn pg-start" onClick={startVerification}>
                Start verification
                <span className="vt-btn-arrow"><ArrowRight size={14} /></span>
              </button>

              <div className="pg-wallets">
                {WALLETS.map(w => <span key={w} className="vt-chip">{w}</span>)}
              </div>
            </div>
          ) : null}

          {step === 'loading' && (
            <div className="pg-card pg-card--center">
              <div className="pg-spinner" />
              <p className="pg-muted">
                {mode === 'redirect' ? 'Redirecting to verification…' : 'Confirming with Veritage…'}
              </p>
            </div>
          )}

          {step === 'result' && (
            <Result wallet={wallet} claims={claims} identity={identity} raw={rawResponse} onReset={reset} />
          )}

          {step === 'error' && (
            <div className="pg-card pg-card--center">
              <div className="pg-error-icon"><Warn size={22} /></div>
              <h2 className="pg-card-title">Verification didn't complete</h2>
              <p className="pg-muted">{errorMsg || 'Something went wrong.'}</p>
              <button className="vt-btn vt-btn--sm" onClick={() => setStep('home')}>
                Try again <span className="vt-btn-arrow"><ArrowRight size={12} /></span>
              </button>
            </div>
          )}
        </section>
      </main>

      <section className="pg-band">
        <WaveLines className="pg-band-waves" lines={24} amplitude={70} color="#BCAFFC" opacity={0.45} />
        <div className="pg-band-inner">
          <div>
            <div className="vt-eyebrow" style={{ color: 'rgba(255,255,255,0.7)' }}>Get started</div>
            <h2>Integrate once. Accept every wallet.</h2>
            <p>One API for EUDI, Google and Apple wallets, with the cryptography and trust checks handled for you.</p>
          </div>
          <div className="pg-band-actions">
            <a className="vt-btn pg-btn-light" href={DOCS_URL}>
              Read the docs <span className="vt-btn-arrow"><ArrowRight size={14} /></span>
            </a>
            <a className="vt-btn vt-btn--ghost pg-btn-outline" href="https://veritage.io/contact/">Book a discovery call</a>
          </div>
        </div>
      </section>

      <footer className="pg-footer">
        <VeritageLogo height={22} />
        <span className="pg-footer-badges">
          <span>GDPR</span><span>ISO 27001</span>
        </span>
        <span className="pg-footer-copy">© {new Date().getFullYear()} Veritage</span>
      </footer>

      {/* Iframe modal — only shown in iframe mode */}
      {mode === 'iframe' && step === 'verifying' && verificationUrl && (
        <>
          <div className="pg-overlay" onClick={reset} aria-hidden="true" />
          <div className="pg-iframe-wrapper" role="dialog" aria-modal="true" aria-label="Veritage identity verification">
            <button type="button" className="pg-iframe-close" onClick={reset} aria-label="Close verification">
              <Close size={18} />
            </button>
            <iframe
              ref={iframeRef}
              src={verificationUrl}
              title="Veritage Identity Verification"
              className="pg-iframe"
              allow="camera; microphone; digital-credentials-get; publickey-credentials-get; publickey-credentials-create"
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals allow-top-navigation-by-user-activation"
            />
          </div>
        </>
      )}
    </div>
  );
}

// ── Rotating headline word (like the veritage.io hero) ───────────────────────

function Rotator({ words }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI(n => (n + 1) % words.length), 2400);
    return () => clearInterval(t);
  }, [words.length]);
  return (
    <span className="pg-rotator" aria-live="off">
      <span key={i} className="pg-rotator-word">{words[i]}</span>
    </span>
  );
}

// ── Code preview ─────────────────────────────────────────────────────────────

function CodePreview({ code }) {
  const [copied, setCopied] = useState(false);
  const [head, body] = [code.split('\n\n')[0], code.split('\n\n').slice(1).join('\n\n')];
  const [requestLine, ...headers] = head.split('\n');
  const [method, ...path] = requestLine.split(' ');
  return (
    <div className="pg-code">
      <div className="pg-code-head">
        <span className="vt-eyebrow">Your backend sends</span>
        <button
          type="button"
          onClick={() => { navigator.clipboard?.writeText(code).catch(() => {}); setCopied(true); setTimeout(() => setCopied(false), 1600); }}
        >
          {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre>
        <span className="tok-method">{method}</span> <span className="tok-url">{path.join(' ')}</span>{'\n'}
        {headers.map(h => {
          const i = h.indexOf(':');
          return <React.Fragment key={h}><span className="tok-key">{h.slice(0, i)}</span>:<span className="tok-str">{h.slice(i + 1)}</span>{'\n'}</React.Fragment>;
        })}
        {'\n'}
        {body.split('\n').map((line, n) => {
          const m = line.match(/^(\s*)"([^"]+)"(:\s*)"([^"]*)"(,?)$/);
          return m
            ? <React.Fragment key={n}>{m[1]}<span className="tok-key">"{m[2]}"</span>{m[3]}<span className="tok-str">"{m[4]}"</span>{m[5]}{'\n'}</React.Fragment>
            : <React.Fragment key={n}>{line}{'\n'}</React.Fragment>;
        })}
      </pre>
    </div>
  );
}

// ── Result ───────────────────────────────────────────────────────────────────

function Result({ wallet, claims, identity, raw, onReset }) {
  const [tab, setTab] = useState('identity');
  const entries = (claims || identity) ? collectClaimEntries(claims, identity) : [];

  return (
    <div className="pg-card pg-card--result">
      <div className="pg-result-head">
        <div className="pg-result-icon"><Check size={22} /></div>
        <div>
          <h2 className="pg-card-title">Identity verified</h2>
          {wallet && (
            <p className="pg-result-wallet">
              via <strong>{wallet.name}</strong>
              {wallet.issuer && <> · {wallet.issuer}</>}
              {wallet.issuingCountry && <> · {wallet.issuingCountry}</>}
            </p>
          )}
        </div>
      </div>

      <div className="pg-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'identity'} className={tab === 'identity' ? 'is-active' : ''} onClick={() => setTab('identity')}>Identity</button>
        <button type="button" role="tab" aria-selected={tab === 'raw'} className={tab === 'raw' ? 'is-active' : ''} onClick={() => setTab('raw')}>Raw response</button>
      </div>

      {tab === 'identity' ? (
        entries.length > 0 ? (
          <dl className="pg-identity">
            {entries.map(({ key, label, value }) => (
              <div className="pg-field" key={key}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        ) : <p className="pg-muted">The wallet didn't disclose any identity fields.</p>
      ) : (
        <pre className="pg-raw">{JSON.stringify(raw, null, 2)}</pre>
      )}

      <div className="pg-result-actions">
        <button className="vt-btn vt-btn--soft vt-btn--sm" onClick={onReset}>Verify again</button>
        <a className="vt-btn vt-btn--sm" href={`${DOCS_URL}/wallet-hub/v1/validate-token`}>
          See the API <span className="vt-btn-arrow"><ArrowUpRight size={12} /></span>
        </a>
      </div>
    </div>
  );
}
